import React, { useEffect, useState } from 'react';

interface ActivityEvent {
    kind: string;
    ts: string;
    subject: string | null;
    detail: string | null;
    extra: string | null;
    ref_id: number;
}

const KIND_META: Record<string, { icon: string; label: string; color: string }> = {
    lead: { icon: '📧', label: 'Lead', color: 'text-blue-600 dark:text-blue-400' },
    unlock: { icon: '🔓', label: 'Unlock', color: 'text-violet-600 dark:text-violet-400' },
    post_published: { icon: '📘', label: 'Post', color: 'text-emerald-600 dark:text-emerald-400' },
    post_failed: { icon: '❌', label: 'Fail', color: 'text-red-600 dark:text-red-400' },
    prompt_ingested: { icon: '📥', label: 'Ingest', color: 'text-amber-600 dark:text-amber-400' },
    fb_comment: { icon: '💬', label: 'Komentarz', color: 'text-cyan-600 dark:text-cyan-400' },
};

function relativeTime(iso: string): string {
    if (!iso) return '—';
    const ts = new Date(iso.includes('T') ? iso : iso.replace(' ', 'T') + 'Z');
    const diffMs = Date.now() - ts.getTime();
    const min = Math.round(diffMs / 60000);
    if (min < 1) return 'teraz';
    if (min < 60) return `${min} min temu`;
    const h = Math.round(min / 60);
    if (h < 24) return `${h} h temu`;
    const d = Math.round(h / 24);
    return `${d}d temu`;
}

export const AktywnoscPage: React.FC = () => {
    const [items, setItems] = useState<ActivityEvent[]>([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState<string | null>(null);
    const [filterKind, setFilterKind] = useState<string>('');
    const [autoRefresh, setAutoRefresh] = useState(true);

    function refresh() {
        const params = new URLSearchParams({ limit: '80' });
        if (filterKind) params.set('kind', filterKind);
        fetch(`/api/admin/activity?${params.toString()}`, { credentials: 'include' })
            .then(r => r.ok ? r.json() : Promise.reject(`HTTP ${r.status}`))
            .then(d => setItems(d.items || []))
            .catch(e => setErr(String(e)))
            .finally(() => setLoading(false));
    }

    useEffect(() => {
        refresh();
        if (!autoRefresh) return;
        const t = setInterval(refresh, 30000);
        return () => clearInterval(t);
    }, [filterKind, autoRefresh]);

    return (
        <div>
            <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
                <h1 className="text-2xl font-bold">Aktywność live</h1>
                <div className="flex gap-2 items-center text-xs">
                    <label className="flex items-center gap-1.5 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={autoRefresh}
                            onChange={e => setAutoRefresh(e.target.checked)}
                        />
                        Auto-odśwież (30s)
                    </label>
                    <button onClick={refresh} className="px-3 py-1.5 bg-blue-500/10 text-blue-600 dark:text-blue-400 hover:bg-blue-500/20 rounded-lg font-semibold">
                        🔄 Teraz
                    </button>
                </div>
            </div>

            <div className="flex gap-3 mb-4 flex-wrap items-center">
                <select
                    value={filterKind}
                    onChange={e => setFilterKind(e.target.value)}
                    className="px-3 py-1.5 text-sm border border-border-light dark:border-border-dark rounded-lg bg-bkg-light dark:bg-bkg-dark"
                >
                    <option value="">Wszystkie typy</option>
                    {Object.entries(KIND_META).map(([k, m]) => (
                        <option key={k} value={k}>{m.icon} {m.label}</option>
                    ))}
                </select>
                <span className="text-xs text-text-secondary-light dark:text-text-secondary-dark ml-auto">
                    {items.length} ostatnich eventów
                </span>
            </div>

            {err && <div className="text-red-500 text-sm mb-4">{err}</div>}
            {loading && items.length === 0 && <div className="text-text-secondary-light dark:text-text-secondary-dark">Ładowanie…</div>}

            <div className="space-y-1.5">
                {items.map((ev, i) => {
                    const meta = KIND_META[ev.kind] || { icon: '•', label: ev.kind, color: '' };
                    return (
                        <div key={`${ev.kind}-${ev.ref_id}-${i}`} className="bg-white dark:bg-content-dark rounded-lg border border-border-light dark:border-border-dark p-3 flex gap-3 items-start text-sm">
                            <span className="text-xl">{meta.icon}</span>
                            <div className="flex-grow min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className={`text-xs font-semibold ${meta.color}`}>{meta.label}</span>
                                    <span className="text-xs text-text-secondary-light dark:text-text-secondary-dark">
                                        {relativeTime(ev.ts)}
                                    </span>
                                    {ev.detail && (
                                        <span className="text-[10px] text-text-secondary-light dark:text-text-secondary-dark font-mono">
                                            {ev.detail.slice(0, 30)}
                                        </span>
                                    )}
                                </div>
                                <div className="font-medium mt-0.5 truncate">{ev.subject || '—'}</div>
                                {ev.extra && (
                                    <div className="text-xs text-text-secondary-light dark:text-text-secondary-dark truncate mt-0.5">
                                        {ev.kind === 'post_published' && ev.extra ? (
                                            <a href={ev.extra} target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 hover:underline">
                                                {ev.extra.slice(0, 80)}
                                            </a>
                                        ) : ev.extra.slice(0, 100)}
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
                {items.length === 0 && !loading && (
                    <div className="text-center py-12 text-text-secondary-light dark:text-text-secondary-dark text-sm">
                        Brak aktywności.
                    </div>
                )}
            </div>
        </div>
    );
};
